/* eslint-disable max-lines */
/* eslint-disable import/no-unused-modules */
import React from 'react';
import _ from 'lodash';
import * as TrackingService from 'common/services/tracking.service';
import { licenseSelector } from 'state/selectors/license';
import { myAccountSelector } from 'state/selectors/account';
import ProvidersOAuthModal from './ProvidersOAuthModal';
import RepositoriesModal from './RepositoriesModal';
import RepositoriesModalV2 from './RepositoriesModalV2';
import HostedGitProviderModal from './HostedGitProviderModal';
import OverdueModal from './OverdueModal';
import ConfirmModal from './ConfirmModal';
import CreateTeamModal from './CreateTeamModal';
import SelectTeamSlackChannelModal from './SelectTeamSlackChannelModal';
import PersonalAccessTokenModal from './PAT/PersonalAccessTokenModal';
import SessionExpiredModal from './SessionExpiredModal';
import LinkAccountModal from './LinkAccountModal/index';
import LinkAccountDoneModal from './LinkAccountModal/LinkAccountDoneModal';
import CustomizationPopover from './CustomizationPopover';
import NotificationCustomizationModal from './NotificationCustomizationModal';
import { getColor } from 'common/constants/_colors';
import { redirectToProvider } from 'common/utils/utils';
import {
  gitIntegrationsSelector,
  pmToolSensorIdSelector
} from 'state/selectors/sensors-authorizations';
import { PM_PROVIDERS, TRACKING } from 'common/constants/constants';
import DataRetentionModal from './DataRetentionModal';
import EditUsersModal, { VIEWS } from './EditUsersModal/EditUserModal';
import { rawContributorsWithSensorAuthSelector } from 'state/selectors/contributors';
import NewConnectionModal from './NewConnectionModal';
import SelectMetricsModal from 'views/root/views/home/views/performance/views/CustomReport/SelectMetricsModal';
import DeleteDashboardModal from 'views/root/views/home/views/performance/views/CustomReport/DeleteDashboardModal/DeleteDashboardModal';
import InvitationSentSuccessfullyModal from './InvitationSentSuccessfullyModal';
import InvitationRequestExpiredModal from './InvitationRequestExpiredModal';
import ReauthModal from './AuthModals/ReauthModal';
import RemoveFromTeamModal from './RemoveFromTeamModal';
import ExistingSlackWorkspaceModal from './ExistingSlackWorkspaceModal';
import CreateEditProjectModal from './CreateEditProjectModal';
import JiraReauthModal from 'views/root/views/home/views/settings/views/ProjectManagement/views/Jira/JiraOAuth1/JiraReauthModal/JiraReauthModal';
import CustomizeTableModal from './CustomizeTableModal/CustomizeTableModal';
import LinkAccountDoneModalJira from './LinkAccountModal/LinkAccountDoneModalJira';
import LinkAccountDoneModalGitProvider from './LinkAccountModal/LinkAccountDoneModalGitProvider';
import FavouritePmToolsModal from './FavouritePmToolsModal';
import CreateApiTokenModal from 'views/root/views/home/views/settings/views/ApiTokens/CreateApiTokenModal';
import AvoidNotificationsDuplicationModal from './AvoidNotificationsDuplicationModal';
import UserInviteModalWithViewer from './UserInviteModalWithViewer';
import ChangeUserTypeModal from './ChangeUserTypeModal';
import ValidatingSamlSSOConfiguration from './ValidatingSamlSSOConfiguration';
import EditCustomPmMetricsModal from './EditCustomPmMetricsModal';
import ExcludeBranchModal from 'views/root/views/home/views/activity/views/branchesV2/modals/ExcludeBranchModal';
import GitStreamWizardModal from './GitStreamWizardModal/GitStreamWizardModal';
import DeleteTeamModal from './DeleteTeamModal';
import { TakeParentIncidentsSettingsModal } from './TakeParentIncidentsSettingsModal/TakeParentIncidentsSettingsModal';
import { ReportedMetricsModal } from './ReportedMetricsModal/ReportedMetricsModal';
import store from 'state/store/configureStore';

const boxShadowOptions = {
  boxShadow: '0px 0px 2.6rem rgba(59, 89, 152, 0.1)',
  border: `1px solid ${getColor('catskillWhite')}`,
  borderRadius: `4px`
};
const backgroundedModalOptions = {
  size: 'backgrounded',
  showCloseButton: false,
  modalParams: {
    hideBackdrop: true,
    disableEnforceFocus: true
  },
  wrapperProps: {
    style: boxShadowOptions
  }
};

export const createModalOptions = (options = {}) => ({
  size: 'normal',
  stateMapper: () => ({}),
  content: () => null,
  showCloseButton: true,
  modalParams: {
    hideBackdrop: false
  },
  ...options
});

export const createHostedGitProviderModalOptions = (props = {}, options = {}) =>
  createModalOptions({
    stateMapper: ({ account, organization, configuration }) => ({
      account,
      organization,
      configuration
    }),
    content: ({ state }) => <HostedGitProviderModal {...props} {...state} />,
    ...backgroundedModalOptions,
    ...options
  });

export const createPersonalAccessTokenModalOptions = (
  props = {},
  options = {}
) =>
  createModalOptions({
    content: ({ state }) => <PersonalAccessTokenModal {...props} {...state} />,
    ...backgroundedModalOptions,
    ...options
  });

export const createGitOAuthModalOptions = (props = {}, options = {}) =>
  createModalOptions({
    stateMapper: ({ account, organization }) => ({
      account,
      organization
    }),
    content: ({ state }) => <ProvidersOAuthModal {...props} {...state} />,
    ...backgroundedModalOptions,
    ...options
  });

export const createOnboardingGitOauthModalOptions = ({
  toggleModal,
  setProvider
}) => {
  const showProvidersModal = ({ setProvider = true } = {}) =>
    toggleModal(
      createOnboardingGitOauthModalOptions({ toggleModal, setProvider })
    );
  return createGitOAuthModalOptions({
    isOnboarding: true,
    setProvider,
    onSelectCloudProvider: ({ provider, withPAT = true }) => {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.ONBOARDING,
        page_tab: TRACKING.PAGE_TABS.GIT_SELECT_PROVIDER,
        click_type: provider
      });
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.ONBOARDING,
        page_tab: TRACKING.PAGE_TABS.GIT_ACCESS_TYPE
      });
      toggleModal(
        createPersonalAccessTokenModalOptions({
          provider,
          withPAT,
          isCloudProvider: true,
          alwaysShowBack: true,
          onSubmit: ({ provider, isPAT }) =>
            isPAT
              ? TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.ONBOARDING,
                  page_tab: TRACKING.PAGE_TABS.GIT_ACCESS_TYPE,
                  click_type: TRACKING.CLICK.PAT
                })
              : redirectToProvider(provider),
          onBack: () => showProvidersModal({ setProvider: false })
        })
      );
    },
    onSelectSelfHostedProvider: ({ provider }) => {
      TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
        page: TRACKING.PAGES.ONBOARDING,
        page_tab: TRACKING.PAGE_TABS.GIT_SELECT_PROVIDER,
        click_type: provider
      });
      toggleModal(
        createHostedGitProviderModalOptions({
          provider,
          onBack: showProvidersModal,
          onSubmit: ({ baseUrl }) =>
            toggleModal(
              createPersonalAccessTokenModalOptions({
                provider,
                baseUrl,
                alwaysShowBack: true,
                onSubmit: ({ provider, isPAT }) =>
                  isPAT ? _.noop : redirectToProvider(provider),
                onBack: () => showProvidersModal({ setProvider: false })
              })
            )
        })
      );
    }
  });
};

export const createRepositoriesModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: ({ organization, sensorAuthorizations, uiState }) => ({
      uiState,
      organization,
      sensorAuthorizations,
      license: licenseSelector({ organization }),
      gitIntegrations: gitIntegrationsSelector({ sensorAuthorizations })
    }),

    content: ({ state }) =>
      props.isFetchLargeRepos ? (
        <RepositoriesModalV2 {...props} {...state} />
      ) : (
        <RepositoriesModal {...props} {...state} />
      ),
    ...backgroundedModalOptions,
    height: '60rem'
  });

export const creatRepositoriesPageAddRepositoryModalOptions = (
  props = {},
  options = {}
) =>
  createModalOptions({
    stateMapper: state => {
      const { organization, repositories, uiState } = state;
      return {
        uiState,
        account: myAccountSelector(state),
        organization,
        repositories,
        license: licenseSelector(state),
        gitIntegrations: gitIntegrationsSelector(state)
      };
    },
    content: ({ state }) =>
      props.isFetchLargeRepos ? (
        <RepositoriesModalV2 {...props} {...state} />
      ) : (
        <RepositoriesModal {...props} {...state} />
      ),
    height: '70rem',
    width: '70rem',
    ...options
  });

export const createOverdueModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <OverdueModal {...props} {...params} />,
    stateMapper: state => {
      const { organization } = state;
      return {
        organization,
        account: myAccountSelector(state)
      };
    },
    ...backgroundedModalOptions,
    width: '60rem',
    height: 'fit-content'
  });

export const createUserInviteModalWithViewerOptions = (props = {}) =>
  createModalOptions({
    stateMapper: ({ accounts }) => ({
      accounts
    }),
    content: params => <UserInviteModalWithViewer {...props} {...params} />,
    height: 'unset',
    minHeight: 'fit-content',
    width: '60rem',
    minWidth: '60rem'
  });
export const createConfirmModalOptions = (props = {}) =>
  createModalOptions({
    size: props.size || 'small',
    content: params => <ConfirmModal checkboxChecked {...props} {...params} />,
    height: 'auto',
    ...(props.width ? { width: props.width } : {})
  });

export const createDeleteTeamOptions = (props = {}) =>
  createModalOptions({
    size: props.size || 'small',
    content: params => <DeleteTeamModal {...props} {...params} />,
    height: 'auto',
    ...(props.width ? { width: props.width } : {})
  });

export const createCreateTeamModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: state => ({
      maxContributors: _.get(licenseSelector(state), 'maxContributors', null)
    }),
    content: params => <CreateTeamModal {...props} {...params} />,
    size: 'medium',
    height: 'auto',
    wrapperProps: {
      style: {
        display: 'flex',
        height: '65rem'
      }
    }
  });

export const createSelectTeamSlackChannelModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <SelectTeamSlackChannelModal {...props} {...params} />,
    size: 'medium',
    height: 'auto',
    wrapperProps: {
      style: {
        display: 'flex',
        height: '65rem',
        width: '50rem',
        minWidth: '50rem'
      }
    }
  });

export const createSessionExpiredModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <SessionExpiredModal {...props} {...params} />,
    size: 'small',
    height: 'auto',
    showCloseButton: false
  });

export const createLinkAccountModalOptions = (props = {}) =>
  createModalOptions({
    content: ({ state }) => <LinkAccountModal {...props} {...state} />,
    ...backgroundedModalOptions,
    size: 'portrait'
  });

export const createLinkAccountDoneModalOptions = (props = {}) =>
  createModalOptions({
    content: ({ state }) => <LinkAccountDoneModal {...props} {...state} />,
    ...backgroundedModalOptions
  });

export const createLinkAccountJiraDoneModalOptions = (props = {}) =>
  createModalOptions({
    content: ({ state }) => <LinkAccountDoneModalJira {...props} {...state} />,
    ...backgroundedModalOptions
  });

export const createLinkAccountGitProviderDoneModalOptions = (props = {}) =>
  createModalOptions({
    content: ({ state }) => (
      <LinkAccountDoneModalGitProvider {...props} {...state} />
    ),
    ...backgroundedModalOptions
  });

export const createCustomizationModalOptions = props =>
  createModalOptions({
    content: params => <CustomizationPopover {...props} {...params} />,
    showCloseButton: false,
    wrapperProps: {
      style: {
        width: '40rem',
        minWidth: '40rem',
        height: 'auto',
        minHeight: 'unset',
        border: '1px solid #D8DEEA',
        boxShadow: '0px 0px 34px rgba(59, 89, 152, 0.2)'
      }
    }
  });

export const createDataRetentionModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: state => ({
      retentionDays: _.get(licenseSelector(state), 'retentionDays', null)
    }),
    content: ({ state }) => <DataRetentionModal {...props} {...state} />,
    showCloseButton: false,
    size: 'small',
    height: 'auto',
    modalParams: {
      hideBackdrop: true
    },
    wrapperProps: {
      style: boxShadowOptions
    }
  });

export const createEditUsersModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: state => ({
      contributors: rawContributorsWithSensorAuthSelector(state)
    }),
    content: ({ state }) => <EditUsersModal {...props} {...state} />,
    size: 'medium',
    height: 'auto',
    wrapperProps: {
      height: props.view === VIEWS.unmerge ? '30rem' : '65rem',
      width: '55rem',
      style: {
        display: 'flex',
        padding: '2rem'
      }
    }
  });

export const createNotificationCutomizationModalOptions = (props = {}) =>
  createModalOptions({
    content: params => (
      <NotificationCustomizationModal {...props} {...params} />
    ),
    wrapperProps: {
      style: {
        width: '46rem',
        minWidth: '35rem',
        height: 'auto',
        minHeight: '20rem'
      }
    }
  });

export const createNewSlackConnectionModalOption = (props = {}) =>
  createModalOptions({
    content: params => <NewConnectionModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'unset',
        width: '50rem',
        minWidth: '20rem'
      }
    }
  });

export const selectMetricsModalOption = (props = {}) => {
  return createModalOptions({
    content: params => <SelectMetricsModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'unset',
        width: '50rem',
        minWidth: '20rem'
      }
    }
  });
};

export const deleteDashboardModalOption = (props = {}) => {
  return createModalOptions({
    content: params => <DeleteDashboardModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'unset',
        width: '34rem',
        minWidth: '20rem'
      }
    }
  });
};
export const createInvitationSentSuccessfullyModalOptions = (props = {}) =>
  createModalOptions({
    content: params => (
      <InvitationSentSuccessfullyModal {...props} {...params} />
    ),
    ...backgroundedModalOptions,
    size: 'portrait'
  });

export const createInvitationRequestExpiredModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <InvitationRequestExpiredModal {...props} {...params} />,
    ...backgroundedModalOptions,
    size: 'portrait'
  });

export const createReauthModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: state => {
      const { repositories } = state;

      return {
        repositories
      };
    },
    content: params => <ReauthModal {...props} {...params} />,
    ...backgroundedModalOptions,
    showCloseButton: true,
    size: 'medium'
  });

export const createRemoveFromTeamModalOptions = (props = {}) => {
  return createModalOptions({
    content: params => <RemoveFromTeamModal {...props} {...params} />,
    size: 'medium',
    height: 'auto',
    wrapperProps: {
      style: {
        width: '35rem',
        minWidth: '35rem',
        height: 'auto'
      }
    }
  });
};

export const createExistingSlackWorkSpaceModalOptions = (props = {}) =>
  createModalOptions({
    stateMapper: () => {
      return {};
    },
    content: params => <ExistingSlackWorkspaceModal {...props} {...params} />,
    wrapperProps: {
      style: {
        width: '32rem',
        minWidth: '32rem',
        height: 'auto',
        minHeight: 'auto'
      }
    }
  });

export const createEditProjectsModalOptions = (props = {}) => {
  const pmProvider = pmToolSensorIdSelector(store.getState());
  const modalSize =
    pmProvider === PM_PROVIDERS.AZURE_BOARDS
      ? {
          width: '100rem',
          minWidth: '100rem'
        }
      : {
          width: '45rem',
          minWidth: '45rem'
        };

  return createModalOptions({
    content: params => <CreateEditProjectModal {...props} {...params} />,
    wrapperProps: {
      style: {
        minHeight: '61rem',
        ...modalSize
      }
    }
  });
};

export const editCustomPmMetricsModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <EditCustomPmMetricsModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'fit-content',
        width: '53rem',
        minWidth: '50rem'
      }
    }
  });

export const reportedMetricsModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <ReportedMetricsModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: '75rem',
        width: '43rem',
        minWidth: '43rem'
      }
    }
  });

export const createCustomizeTableModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <CustomizeTableModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: '52rem',
        width: '48rem',
        minWidth: '48rem'
      }
    }
  });

export const createJiraReauthModal = (props = {}) =>
  createModalOptions({
    content: params => <JiraReauthModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'fit-content',
        width: '50rem',
        minWidth: '50rem'
      }
    }
  });

export const createFavouritePmToolsOptionsModal = props =>
  createModalOptions({
    content: params => <FavouritePmToolsModal {...props} {...params} />,
    showCloseButton: true,
    wrapperProps: {
      style: {
        width: '40rem',
        minWidth: '40rem',
        height: 'auto',
        minHeight: 'unset',
        border: '1px solid #D8DEEA',
        boxShadow: '0px 0px 34px rgba(59, 89, 152, 0.2)'
      }
    }
  });

export const createCreateApiTokenModal = (props = {}) =>
  createModalOptions({
    content: params => <CreateApiTokenModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'fit-content',
        width: '40rem',
        minWidth: '40rem'
      }
    }
  });

export const createAvoidNotificationsDuplicationModalOptions = (props = {}) =>
  createModalOptions({
    content: params => (
      <AvoidNotificationsDuplicationModal {...props} {...params} />
    ),
    wrapperProps: {
      style: {
        width: '40rem',
        minWidth: '35rem',
        height: 'auto',
        minHeight: 'fit-content'
      }
    }
  });

export const changeUserTypeModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <ChangeUserTypeModal {...props} {...params} />,
    wrapperProps: {
      style: {
        width: '35rem',
        minWidth: '35rem',
        height: 'auto',
        minHeight: 'fit-content'
      }
    }
  });

export const createValidatingSamlSSOConfigration = (props = {}) =>
  createModalOptions({
    content: params => (
      <ValidatingSamlSSOConfiguration {...props} {...params} />
    ),
    wrapperProps: {
      style: {
        width: '50rem',
        minWidth: '50rem',
        height: 'auto',
        minHeight: 'fit-content'
      }
    }
  });

export const excludeBranchModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <ExcludeBranchModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: 'unset',
        minHeight: 'unset',
        width: '34rem',
        minWidth: '20rem'
      }
    }
  });
export const gitStreamWizardModalOptions = (props = {}) =>
  createModalOptions({
    content: params => <GitStreamWizardModal {...props} {...params} />,
    wrapperProps: {
      style: {
        height: '56.4rem',
        width: '75.6rem'
      }
    }
  });
export const takeParentIncidentsSettingsModalOptions = (props = {}) =>
  createModalOptions({
    size: 'small',
    content: params => (
      <TakeParentIncidentsSettingsModal {...props} {...params} />
    ),
    wrapperProps: {
      style: {
        height: '8.4rem',
        width: '15.6rem',
        minHeight: '25rem',
        minWidth: '55rem'
      }
    }
  });
